import React,{useState,useEffect,useCallback,useRef,useMemo,useLayoutEffect} from 'react';
import {motion,useReducedMotion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import {FiChevronLeft,FiChevronRight} from 'react-icons/fi';

function AutoScrollCarousel({items,renderItem,autoPlayInterval=5000,className="",mode="full",onIndexChange}) {
  const [currentIndex,setCurrentIndex]=useState(0);
  const [isPaused,setIsPaused]=useState(false);
  const [visibleCount,setVisibleCount]=useState(1);
  const [slotWidth,setSlotWidth]=useState(0);
  
  const timerRef=useRef(null);
  const containerRef=useRef(null);
  const viewportRef=useRef(null);
  const prefersReducedMotion=useReducedMotion();
  
  const isSidebar=mode==="sidebar";
  const total=items?.length || 0;

  // Handle responsive visible count and pixel measurement
  useLayoutEffect(()=> {
    const handleResize=()=> {
      const width=window.innerWidth;
      let count=1;
      
      if (!isSidebar) {
        if (width >=1024) count=3;
        else if (width >=768) count=2;
      }
      
      const safeCount = Math.min(count, total || 1);
      setVisibleCount(safeCount);

      if (viewportRef.current) {
        const vWidth = viewportRef.current.offsetWidth;
        setSlotWidth(vWidth / safeCount);
      }
    };

    handleResize();
    window.addEventListener('resize',handleResize);
    return ()=> window.removeEventListener('resize',handleResize);
  },[isSidebar, total]);

  useEffect(()=> {
    if (onIndexChange) {
      onIndexChange(currentIndex % total);
    }
  },[currentIndex,total,onIndexChange]);

  const nextSlide=useCallback(()=> {
    if (total <= 1) return;
    setCurrentIndex((prev)=> (prev + 1) % total);
  },[total]);

  const prevSlide=useCallback(()=> {
    if (total <= 1) return;
    setCurrentIndex((prev)=> (prev - 1 + total) % total);
  },[total]);

  useEffect(()=> {
    if (!isPaused && !prefersReducedMotion && total > 1) {
      timerRef.current=setInterval(nextSlide,autoPlayInterval);
    } 
    return ()=> clearInterval(timerRef.current);
  },[isPaused,nextSlide,autoPlayInterval,prefersReducedMotion, total]);

  const handleKeyDown=(e)=> {
    if (e.key==='ArrowLeft') prevSlide();
    if (e.key==='ArrowRight') nextSlide();
  };

  const displayItems=useMemo(()=> {
    if (total === 0) return [];
    // Buffer items to handle smooth visual transitions at ends
    return [...items,...items.slice(0,visibleCount)];
  },[items,visibleCount,total]);

  if (total === 0) return null;

  return ( 
    <div 
      ref={containerRef} 
      className={`relative group w-full ${className}`} 
      onMouseEnter={()=> setIsPaused(true)} 
      onMouseLeave={()=> setIsPaused(false)} 
      onFocusCapture={()=> setIsPaused(true)} 
      onBlurCapture={()=> setIsPaused(false)} 
      onKeyDown={handleKeyDown} 
      tabIndex="0" 
      aria-roledescription="carousel" 
    > 
      <div ref={viewportRef} className="overflow-hidden px-1 py-4"> 
        <motion.div 
          className="flex" 
          animate={{ x: -(currentIndex * slotWidth) }} 
          transition={{ type: "spring", stiffness: 300, damping: 35 }} 
          style={{ width: slotWidth * displayItems.length }} 
        > 
          {displayItems.map((item,idx)=> ( 
            <div 
              key={`${item.id}-${idx}`} 
              className="flex-shrink-0 px-2 sm:px-3" 
              style={{ width: slotWidth }} 
            > 
              <div className="h-full w-full"> 
                {renderItem(item)} 
              </div> 
            </div> 
          ))} 
        </motion.div> 
      </div> 

      {/* Navigation Controls */}
      <div className={`flex justify-center items-center ${isSidebar ? 'mt-2' : 'mt-4 sm:mt-6'} space-x-6`}> 
        <button 
          onClick={prevSlide} 
          className={`${isSidebar ? 'p-2' : 'p-3 sm:p-4'} rounded-full bg-white border border-slate-200 text-slate-600 hover:text-blue-600 hover:border-blue-200 shadow-sm transition-all focus:ring-2 focus:ring-blue-500 min-w-[44px] min-h-[44px] flex items-center justify-center active:scale-95 disabled:opacity-50`} 
          aria-label="Previous"
          disabled={total <= 1}
        > 
          <SafeIcon icon={FiChevronLeft} className={isSidebar ? 'h-4 w-4' : 'h-5 w-5 sm:h-6 sm:w-6'} /> 
        </button> 

        <button 
          onClick={nextSlide} 
          className={`${isSidebar ? 'p-2' : 'p-3 sm:p-4'} rounded-full bg-white border border-slate-200 text-slate-600 hover:text-blue-600 hover:border-blue-200 shadow-sm transition-all focus:ring-2 focus:ring-blue-500 min-w-[44px] min-h-[44px] flex items-center justify-center active:scale-95 disabled:opacity-50`} 
          aria-label="Next"
          disabled={total <= 1}
        > 
          <SafeIcon icon={FiChevronRight} className={isSidebar ? 'h-4 w-4' : 'h-5 w-5 sm:h-6 sm:w-6'} /> 
        </button> 
      </div> 
    </div> 
  );
}

export default AutoScrollCarousel;